import {Component, OnInit} from '@angular/core';
import {filter, take} from 'rxjs';
import {AuthService} from '../../../_services/auth.service';
import {Company} from '../../../_models/company';
import {EffectiveRole, User} from '../../../_models/user';
import {CompanyWorkspaceService} from '../../../_services/company-workspace.service';
import {GlobalStore} from '../../../global.store';
import {TargetCompanyService} from '../../../_services/target-company.service';

@Component({
  selector: 'app-welcome-screen',
  templateUrl: './welcome-screen.component.html',
  styleUrls: ['../auth.scss', './welcome-screen.component.scss'],
  standalone: false
})
export class WelcomeScreenComponent implements OnInit {
  readonly roleLabels: Record<string, string> = {
    [EffectiveRole.ROLE_COMPANY_ADMIN]: 'Admin',
    [EffectiveRole.ROLE_USER]: 'User',
    [EffectiveRole.ROLE_TRADE]: 'Trade'
  };

  loading = true;
  isSelecting = false;
  selectedCompanyId: number | null = null;
  shouldRememberSelection = false;
  currentUser: User | null = null;
  companies: Company[] = [];

  constructor(
    private authService: AuthService,
    private companyWorkspaceService: CompanyWorkspaceService,
    private globalStore: GlobalStore,
    private targetCompanyService: TargetCompanyService
  ) {
  }

  ngOnInit(): void {
    this.globalStore.currentUser$
      .pipe(
        filter((user) => user !== undefined),
        take(1)
      )
      .subscribe((user) => {
        this.currentUser = user;
        this.loading = false;

        if (!user) {
          this.authService.logout(true);
          return;
        }

        if (!this.companyWorkspaceService.isAppWorkspaceHost()) {
          this.companyWorkspaceService.navigateToDestination(
            this.companyWorkspaceService.resolvePostLoginDestination(user)
          );
          return;
        }

        if (!this.companyWorkspaceService.shouldShowWelcomeScreen(user)) {
          this.companyWorkspaceService.navigateToDestination(
            this.companyWorkspaceService.resolvePostLoginDestination(user)
          );
          return;
        }

        const preferredCompany = this.companyWorkspaceService.resolvePreferredCompany(user);
        if (preferredCompany) {
          this.targetCompanyService.setTargetCompanyId(preferredCompany.id);
          this.navigateToCompanyDestination(preferredCompany);
          return;
        }

        this.companies = [...(user.companies ?? [])];
      });
  }

  get userName(): string {
    return this.currentUser?.name || this.currentUser?.email || 'User';
  }

  get hasCompanies(): boolean {
    return this.companies.length > 0;
  }

  get contentClassName(): string {
    return this.hasCompanies ? 'welcome-screen__content--companies' : 'welcome-screen__content--empty';
  }

  get isScrollableTable(): boolean {
    return this.companies.length >= 6;
  }

  get skeletonRows(): number[] {
    return Array.from({length: 6}, (_, index) => index);
  }

  trackByCompanyId(_: number, company: Company): number {
    return company.id;
  }

  getProjectsLabel(projectsCount: number | null | undefined): string {
    const normalizedProjectsCount = projectsCount ?? 0;
    return `${normalizedProjectsCount} projects`;
  }

  getRoleLabel(company: Company): string {
    return this.roleLabels[company.companyRole] ?? '-';
  }

  getRoleClass(company: Company): string {
    switch (company.companyRole) {
      case EffectiveRole.ROLE_COMPANY_ADMIN:
        return 'welcome-screen__role--admin';
      case EffectiveRole.ROLE_TRADE:
        return 'welcome-screen__role--trade';
      default:
        return 'welcome-screen__role--user';
    }
  }

  onSelectCompany(company: Company): void {
    if (!company?.id || this.isSelecting) {
      return;
    }

    this.isSelecting = true;
    this.selectedCompanyId = company.id;
    this.targetCompanyService.setTargetCompanyId(company.id);

    if (this.shouldRememberSelection) {
      this.companyWorkspaceService.savePreferredCompany(company.id);
    } else {
      this.companyWorkspaceService.clearPreferredCompany();
    }

    this.navigateToCompanyDestination(company);
  }

  onLogout(): void {
    this.authService.logout();
  }

  private navigateToCompanyDestination(company: Company): void {
    const destination = this.companyWorkspaceService.getCompanyDestination(company, false, this.currentUser);
    window.location.replace(destination.url);
  }
}
